import React, { ReactNode, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useJWTAuth } from '../contexts/useJWTAuth';
import { ThemeSwitcher } from './ThemeSwitcher';
import { CupidNavButton } from './CupidNavButton';

interface NavbarProps {
  pageTitle?: string;
  showDivider?: boolean;
  leftContent?: ReactNode; // Custom content to add after logo
  rightContent?: ReactNode; // Custom content to add before theme switcher
  children?: ReactNode; // Alternative way to add custom content
  className?: string; // Allow custom styling
  containerClassName?: string; // Custom container styling
  showAuthButton?: boolean; // Show sign in button for unauthenticated users
}

export const Navbar: React.FC<NavbarProps> = ({
  pageTitle,
  showDivider = false,
  leftContent,
  rightContent,
  children,
  className = '',
  containerClassName = '',
  showAuthButton = true,
}) => {
  const { isAuthenticated, user, signOut } = useJWTAuth();
  const isAutarAdmin = user?.isPlatformAdmin ?? false;
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Determine which auth link to show based on current page
  const isOnSignInPage = location.pathname === '/signin';

  const handleSignOut = async () => {
    await signOut();
    setShowUserMenu(false);
    navigate('/');
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 border-b border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xs px-4 sm:px-6 lg:px-8 ${className}`}
      >
        <div className={`max-w-7xl mx-auto py-4 ${containerClassName}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              {/* Logo and Product Name */}
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                <img
                  src="/Autar_logo_256x256.png"
                  alt="Autar Logo"
                  className="h-8 w-8 rounded-full"
                />
                <span className="text-xl font-semibold text-gray-900 dark:text-white">Autar</span>
                <span className="text-sm px-2 py-1 rounded-full bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400">
                  Alpha
                </span>
              </Link>

              {/* Optional Page Title with Divider */}
              {pageTitle && showDivider && (
                <>
                  <div className="h-6 w-px bg-gray-300 dark:bg-gray-600"></div>
                  <h1 className="text-lg font-medium text-gray-700 dark:text-gray-300">
                    {pageTitle}
                  </h1>
                </>
              )}

              {/* Custom Left Content */}
              {leftContent && (
                <>
                  {!pageTitle && <div className="h-6 w-px bg-gray-300 dark:bg-gray-600"></div>}
                  {leftContent}
                </>
              )}
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-3">
              {/* Organizations (fleet management) */}
              {isAuthenticated && (
                <button
                  onClick={() => navigate('/orgs')}
                  className="px-3 py-1.5 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  Organizations
                </button>
              )}

              {/* Admin Dashboard link — visible only to platform admins */}
              {isAuthenticated && isAutarAdmin && (
                <button
                  onClick={() => navigate('/admin/scans')}
                  className="px-3 py-1.5 text-sm font-medium rounded-lg bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 hover:bg-teal-100 dark:hover:bg-teal-900/50 transition-colors"
                >
                  Admin
                </button>
              )}

              {isAuthenticated && <CupidNavButton />}

              {/* Custom Right Content */}
              {rightContent}

              {/* Theme Switcher */}
              <ThemeSwitcher />

              {/* Authentication UI */}
              {isAuthenticated && user ? (
                // Authenticated User - Show user menu
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 rounded-full border-2 border-teal-200 dark:border-teal-700 hover:border-teal-300 dark:hover:border-teal-600 transition-colors p-1"
                  >
                    <div className="w-8 h-8 rounded-full bg-teal-600 dark:bg-teal-500 flex items-center justify-center text-white font-semibold">
                      {user.firstName
                        ? user.firstName[0].toUpperCase()
                        : user.email[0].toUpperCase()}
                    </div>
                  </button>

                  {/* Dropdown Menu */}
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-56 rounded-lg shadow-lg bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border border-gray-200 dark:border-gray-700 py-1">
                      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {user.firstName || 'User'}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                          {user.email}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          navigate('/settings');
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-teal-50 dark:hover:bg-teal-900/30 hover:text-teal-700 dark:hover:text-teal-400 transition-colors"
                      >
                        Settings
                      </button>
                      <button
                        onClick={handleSignOut}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-teal-50 dark:hover:bg-teal-900/30 hover:text-teal-700 dark:hover:text-teal-400 transition-colors"
                      >
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                // Unauthenticated User UI - Show appropriate auth link based on current page
                showAuthButton &&
                (isOnSignInPage ? (
                  <Link to="/signup" className="btn-primary px-4 py-2 text-sm font-medium">
                    Create Account
                  </Link>
                ) : (
                  <Link to="/signin" className="btn-secondary px-4 py-2 text-sm font-medium">
                    Sign In
                  </Link>
                ))
              )}
            </div>
          </div>

          {/* Children content (if provided) - renders below the main navbar */}
          {children}
        </div>
      </header>

      {/* Click outside to close menu */}
      {showUserMenu && (
        <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
      )}
    </>
  );
};
